<script setup>
import Header from "./Header.vue"
import HistoryComponentBox from "./HistoryComponentBox.vue"
import api from "../api/api.js"
import ClipLoader from "vue-spinner/src/ClipLoader.vue"
import ButtonCancel from "./ButtonCancel.vue"
import ButtonSubmit from "./ButtonSubmit.vue"
import { readDate } from '../utils/timezone.js';
</script>

<template>
  <div class="body-container">
    <header>
      <h1>History</h1>
      <h2>H01</h2>
    </header>

    <div v-if="isLoadingHistory">
      <clip-loader :loading="true" :color="setColor" :size="setSize"></clip-loader>
    </div>

    <v-data-table
      v-else
      :headers="columns"
      :items="callsArr"
      class="elevation-1"
      item-key="CallerID"
      id="table"
      :items-per-page="pagination.itemsPerPage"
      v-model:pagination="pagination"
      v-model:page="pagination.page"
    >
      <template v-slot:item="{ item }">
          <tr
            :key="item.MemberNumber"
            :class="{
              notes_row: item.IsHeader === 1,
              header_row: item.IsHeader === 1,
              non_header_row: item.IsHeader !== 1,
            }"
            :style="{
              'background-color': item.IsHeader === 1 ? '#ffffff' : '#d9d9d9',
          }">
          <td
            style="border: 1px solid black; background-color: #ffffff"
            v-if="item.IsHeader === 1">
            {{ item.CallerID }}
          </td>
          <td class="arrow-cell" style="border: 1px solid black; padding: 0" v-else>
            <img :src="getArrowImage()" alt="" />
          </td>
          <td style="border: 1px solid black">{{ item.MemberNumber }}</td>
          <td style="border: 1px solid black">{{ item.SourceIp }}</td>
          <td style="border: 1px solid black">{{ item.MemberName }}</td>
          <td style="border: 1px solid black">{{readDate(item.CallStartDateTime, parsedLoginUp.time_zone)}}</td>
          <td
            class="text-center"
            style="border: 1px solid black"
            :style="{
              'background-color': item.IsHeader === 1 ? '#ffffff' : '',
              color: item.IsHeader === 1 ? 'black' : '',
            }">
            <div v-if="item.IsHeader === 1 && item.withNote === 1">
              <ButtonSubmit label="View Notes" @click="openModal(item.InteractionId)" />
            </div>
          </td>
          <td style="border: 1px solid black">{{ item.Authentication }}</td>
          <td style="border: 1px solid black">{{ item.FailureReason }}</td>
          <td style="border: 1px solid black">{{ item.AgentName }}</td>
          <td style="border: 1px solid black">{{ item.InteractionId }}</td>
          </tr>
      </template>
    </v-data-table>

    <dialog :open="modalOpen">
      <div class="dialog-backdrop"></div>
      <div class="dialog-body">
        <form method="dialog">
          <button type="button" class="close" @click="modalOpen = false">
            <span>&times;</span>
          </button>
          <div v-if="isLoadingNotes">
            <clip-loader :loading="true" :color="setColor" :size="setSize"></clip-loader>
          </div>
          <h4 class="dialog-header">Notes for {{ interactionId }}</h4>
          <table id="table" class="table table-sm table-bordered table-hover border-dark">
            <thead>
              <tr class="notes_row table-primary table-bordered">
                <th style="border: 1px solid black" class="table-secondary">Category</th>
                <th style="border: 1px solid black" class="table-secondary">Notes</th>
                <th style="border: 1px solid black" class="table-secondary">Agent</th>
                <th style="border: 1px solid black" class="table-secondary">Date Created</th>
                <th style="border: 1px solid black" class="table-secondary">Date Modified</th>
              </tr>
            </thead>
            <tbody v-if="!isLoadingNotes">
              <tr v-for="noteRow in notes.result" :key="noteRow.id" class="notes_row">
                <td style="border: 1px solid black; text-align: left">
                  {{ noteRow.NoteCategory }}
                </td>
                <td
                  style="border: 1px solid black; text-align: left"
                  v-html="noteFormattedText(noteRow.NoteText)"></td>
                <td style="border: 1px solid black; text-align: left">
                  {{ noteRow.AgentID }}
                </td>
                <td style="border: 1px solid black; text-align: left">
                  {{ readDate(noteRow.CreatedAt, parsedLoginUp.time_zone) }}
                </td>
                <td style="border: 1px solid black; text-align: left">
                  {{ readDate(noteRow.ModDateTime, parsedLoginUp.time_zone) }}
                </td>
              </tr>
            </tbody>
          </table>
          <div class="button-group" style="margin-top: 20px">
            <ButtonSubmit label="Close" @click="modalOpen = false" />
          </div>
        </form>
      </div>
    </dialog>
  </div>
</template> 

<script>
// Import arrow images once at the component level
import arrowLowImg from '@/assets/arrowlow.png'
import arrowMidImg from '@/assets/arrowmid.png'
import arrowHighImg from '@/assets/arrowhigh.png'

export default {
  name: "HistoryComponent",
  component: [Header, HistoryComponentBox, ButtonCancel, ButtonSubmit],
  data: () => {
    return {
      // Preload images to avoid dynamic require
      arrowImages: {
        low: arrowLowImg,
        mid: arrowMidImg,
        high: arrowHighImg
      },
      currentArrowSize: 'low',
      resizeTimer: null,
      setColor: "#007bff",
      setSize: "50px",
      isLoadingHistory: false,
      isLoadingNotes: false,
      hasNotesData: [],
      modalOpen: false,
      interactionId: "example-interaction-id",
      notes: {
        status: "",
        num_rows: "",
        result: {},
      },
      company: localStorage.getItem('companyID'),
      authToken: localStorage.getItem("authToken"),
      callsArr: [],
      columns: [
        { key: "CallerID", title: "Caller ID" },
        { key: "MemberNumber", title: "Member #", sortable: false },
        { key: "SourceIp", title: "Source IP", sortable: false},
        { key: "MemberName", title: "Member Name", sortable: false},
        { key: "CreatedAt", title: "Date", sortable: false },
        { key: "", title: "Note" },
        { key: "AuthStatus", title: "Status", sortable: false },
        { key: "FailureReason", title: "Failure Reason", sortable: false },
        { key: "AgentName", title: "Agent", sortable: false },
        { key: "InteractionId", title: "Interaction ID", sortable: false },
      ],
    pagination: {
      page: 1,
      itemsPerPage: 10,
    },
      localStorageLoginUp: localStorage.getItem("passLoginUp"),
      parsedLoginUp: localStorage.getItem("passLoginUp"),
    }
  },
  computed: {
    noteFormattedText() {
      return function (text) {
        return text.replace(/\\n/g, "<br>")
      }
    },
  },
  props: {
    userData: Object,
    brandData: Object,
  },
  created() {
    // Parse localStorage data once during component creation
    try {
      this.parsedLoginUp = JSON.parse(this.localStorageLoginUp);
    } catch (error) {
      console.error("Failed to parse localStorageLoginUp:", error);
    }
    
    // Set brand color
    if (this.brandData && this.brandData.accent_color1) {
      this.setColor = this.brandData.accent_color1;
    }
  },
  mounted() {
    // Throttled resize handler using a timer
    const handleResize = () => {
      if (this.resizeTimer) {
        clearTimeout(this.resizeTimer);
      }
      
      this.resizeTimer = setTimeout(() => {
        this.updateArrowImages();
      }, 200); // Throttle to run at most every 200ms
    };
    
    // Add resize listener with throttled handler
    window.addEventListener('resize', handleResize);
    
    // Immediately fetch history data
    this.fetchHistoryData();
    
    // Update arrow images after initial render
    this.$nextTick(() => {
      this.updateArrowImages();
    });
  },
  beforeUnmount() {
    // Proper cleanup of event listeners
    window.removeEventListener('resize', () => {});
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer);
    }
  },
  methods: {
    // Helper to get the current arrow image based on cell height
    getArrowImage() {
      return this.arrowImages[this.currentArrowSize];
    },
    
    // Update arrow images based on cell heights - runs once per resize
    updateArrowImages() {
      const arrowCells = document.querySelectorAll('.arrow-cell');
      if (arrowCells.length === 0) return;
      
      // Check first cell's height to determine which arrow to use
      // This is more efficient than checking each cell
      const cellHeight = arrowCells[0].clientHeight;
      
      if (cellHeight >= 80) {
        this.currentArrowSize = 'high';
      } else if (cellHeight >= 59) {
        this.currentArrowSize = 'mid';
      } else {
        this.currentArrowSize = 'low';
      }
    },

    async fetchHistoryData() {
      try {
        this.isLoadingHistory = true;
        
        // Get caller info
        const callerInfo = await api.post("getCallerInfoById", {
          company: this.company,
          indexNum: this.authToken,
        });

        // Get authenticated calls
        const resAuthCalls = await api.get(
          `getAuthCallsById/${this.company}/${callerInfo.data.CallerID.replace('+','')}/${callerInfo.data.MemberNumber}`
        );

        console.log("resAuthCalls", callerInfo)
        this.callsArr = resAuthCalls.data;
        console.log("resAuthCalls.data", resAuthCalls.data)
        console.log("this.authToken", this.authToken)
      } catch (error) {
        console.error("Error fetching history data:", error);
      } finally {
        this.isLoadingHistory = false;
        
        // Update arrow images after data is loaded
        this.$nextTick(() => {
          this.updateArrowImages();
        });
      }
    },

    openModal(interactionId) {
      this.isLoadingNotes = true;
      this.modalOpen = true;
      this.interactionId = interactionId;
      this.GetFraudNotesByInteraction(this.company, interactionId);
    },

    async GetFraudNotesByInteraction(company, interactionid) {
      try {
        const { status, data } = await api.post("getFraudNotesByInteractionId", {
          InteractionId: interactionid,
          Company: company,
        });

        if (status === 200) {
          this.notes.result = data;
        }
      } catch (error) {
        console.error("Error fetching notes:", error);
      } finally {
        this.isLoadingNotes = false;
      }
    },
  },
}
</script>

<style scoped>
header {
  display: flex;
  justify-content: space-between;
}
table {
  width: 100%;
  border-collapse: collapse;
}

th {
  text-align: left;
  padding: 8px;
  cursor: pointer;
}

.sortable:hover {
  background-color: #f2f2f2;
}

th.active {
  font-weight: bold;
}

.sort-indicator {
  margin-left: 4px;
}

td {
  padding: 8px;
  border-top: 1px solid #ddd;
}

h1 {
  font-size: 24px;
}

.description {
  font: -webkit-control;
}

.body-container {
  align-self: unset;
  min-width: 1100px;
}

.row {
  margin: 1.5rem 0;
}
tr td:nth-child(1) {
  text-align: right;
  display: table-cell;
  min-width: 100px; /* Minimum width */
  max-width: 200px; /* Maximum width */
}

tr td:nth-child(5) {
  padding: 8px 4px !important;
}

tr th:nth-child(1) {
  width: 8%;
}

dialog {
  border: 1px solid rgba(0, 0, 0, 0.2);
  padding: 0;
  position: fixed;
  left: 0;
  top: 10%;
  background-color: #fff;
  z-index: 100;
  box-shadow: 2px 4px #ccc;
  border: 2px solid #767676;
}

.dialog-header {
  margin: 0;
  margin-bottom: 16px;
  font-size: 24px;
}

.dialog-backdrop {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.2);
  z-index: -10;
}

.dialog-body {
  z-index: 10;
  background-color: #fff;
  padding: 2rem;
  overflow-y: auto; /* Add a vertical scroll bar when content exceeds the height */
  max-height: 80vh; /* Limit the maximum height of the dialog body */
}
</style>